import React from 'react';
import { Typography, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import './GuestTaskList.less';

const { Text } = Typography;

/**
 * 默认状态配置
 */
const DEFAULT_STATUS_CONFIG = {
    0: { text: '未反馈', type: 'secondary' },
    1: { text: '已确认', type: 'success' },
    2: { text: '已拒绝', type: 'danger' },
    3: { text: '待确认', type: 'warning' },
};

/**
 * 嘉宾会议任务列表组件
 * @param {Object} props - 组件属性
 * @param {Array} props.agendasGuestsList - 嘉宾日程列表
 * @param {boolean} props.expanded - 是否展开显示所有任务
 * @param {Function} props.onToggleExpansion - 切换展开状态的回调函数
 * @param {Object} props.record - 当前记录对象（用于展开状态管理）
 * @param {Object} props.statusConfig - 状态配置对象（可选），用于自定义状态显示，默认使用内置配置
 */
const GuestTaskList = ({ agendasGuestsList = [], expanded = false, onToggleExpansion, record, statusConfig = DEFAULT_STATUS_CONFIG }) => {
    // 如果没有任务数据，显示占位符
    if (!agendasGuestsList || !agendasGuestsList.length) {
        return <Text type="secondary">&ndash;</Text>;
    }

    const agendaDates = prepareAgendaTasksLevel(agendasGuestsList);
    const totalTasks = agendasGuestsList.length;
    const isExpanded = expanded;

    return (
        <div className="guest-task-list">
            {agendaDates.map((agendaDate, dateIndex) => (
                <div key={dateIndex} className="conference-hall-group">
                    {agendaDate.conferenceHalls.map((hall, hallIndex) => {
                        // 计算当前场次在所有场次中的索引
                        const hallGlobalIndex = agendaDates.slice(0, dateIndex).reduce((sum, d) => sum + d.conferenceHalls.length, 0) + hallIndex;

                        // 默认只显示第一个场次
                        const shouldShowHall = hallGlobalIndex === 0 || isExpanded;

                        if (!shouldShowHall) return null;

                        return (
                            <div key={hallIndex} className="conference-hall">
                                {/* 会议场次标题 */}
                                <div className="hall-header">
                                    <span className="hall-date">{agendaDate.date}</span>
                                    <span className="hall-name">{hall.hallName}</span>
                                </div>

                                {/* 任务列表 */}
                                <ul className="agenda-list">
                                    {hall.agendas.map((agenda, agendaIndex) => {
                                        // 计算当前任务在所有任务中的索引
                                        const globalIndex = agendaDates.slice(0, dateIndex).reduce((sum, d) => sum + d.conferenceHalls.reduce((hSum, h) => hSum + h.agendas.length, 0), 0) + agendaDates[dateIndex].conferenceHalls.slice(0, hallIndex).reduce((sum, h) => sum + h.agendas.length, 0) + agendaIndex;

                                        // 默认只显示第一个任务
                                        const shouldShowTask = globalIndex === 0 || isExpanded;

                                        if (!shouldShowTask) return null;

                                        const status = getInvitationStatus(agenda.invitationAccepted, agenda.refusalInfo, statusConfig);

                                        return (
                                            <li key={agendaIndex} className="agenda-item">
                                                <div className="agenda-content">
                                                    <span className="agenda-time">{agenda.timeRange}</span>
                                                    <span className="agenda-title">{agenda.agendaTitle}</span>
                                                    <Tag color="blue" className="task-tag">
                                                        {agenda.taskName}
                                                    </Tag>
                                                    {status.tooltip ? (
                                                        <Tooltip placement="top" trigger="click" title={status.tooltip}>
                                                            <Text type={status.type} className="status-text clickable">
                                                                {status.text}
                                                            </Text>
                                                        </Tooltip>
                                                    ) : (
                                                        <Text type={status.type} className="status-text">
                                                            {status.text}
                                                        </Text>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            ))}

            {/* 展开/收起按钮 */}
            {totalTasks > 1 && (
                <div className="toggle-expansion" onClick={() => onToggleExpansion && onToggleExpansion(record)}>
                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                        <i className="bi bi-caret-down-fill"></i>
                    </span>
                    <span className="expand-text">{isExpanded ? '收起' : `展开全部${totalTasks}个任务`}</span>
                </div>
            )}
        </div>
    );
};

/**
 * 处理日程任务数据，按日期和会议厅分组
 * @param {Array} tasks - 任务列表
 * @returns {Array} 处理后的日程日期数据
 */
const prepareAgendaTasksLevel = tasks => {
    const agendaDates = [];

    tasks.forEach(t => {
        const date = dayjs(t.startTime).isValid() ? dayjs(t.startTime).format('M月D日') : '';
        const startTime = dayjs(t.startTime).isValid() ? dayjs(t.startTime).format('HH:mm') : '';
        const endTime = dayjs(t.endTime).isValid() ? dayjs(t.endTime).format('HH:mm') : '';

        const agenda = {
            agendaId: t.agendaId,
            agendaTitle: t.title,
            timeRange: startTime && endTime ? `${startTime}～${endTime}` : '',
            taskName: t.taskName || '',
            invitationAccepted: t.invitationAccepted,
            refusalInfo: t.refusalInfo,
        };

        // 查找是否已存在该日期
        let existingDate = agendaDates.find(d => d.date === date);
        if (!existingDate) {
            existingDate = {
                date: date,
                conferenceHalls: [],
            };
            agendaDates.push(existingDate);
        }

        // 查找是否已存在该会议厅
        let existingHall = existingDate.conferenceHalls.find(h => h.hallId === t.conferenceHallId);
        if (!existingHall) {
            existingHall = {
                hallId: t.conferenceHallId,
                hallName: t.conferenceHallName,
                place: t.place,
                agendas: [],
            };
            existingDate.conferenceHalls.push(existingHall);
        }

        existingHall.agendas.push(agenda);
    });

    return agendaDates;
};

/**
 * 获取邀请确认状态的文本和样式
 * @param {number} status - 邀请状态
 * @param {string} refusalInfo - 拒绝信息
 * @param {Object} statusConfig - 状态配置对象
 * @returns {Object} 状态信息对象
 */
const getInvitationStatus = (status, refusalInfo, statusConfig = DEFAULT_STATUS_CONFIG) => {
    // 使用提供的状态配置或默认配置
    const config = statusConfig[status];

    if (config) {
        return {
            text: config.text,
            type: config.type,
            tooltip: status === 2 ? refusalInfo : config.tooltip,
        };
    }

    // 兜底配置
    return { text: '待确认', type: 'secondary' };
};

export default GuestTaskList;
